/* eslint-disable react-hooks/exhaustive-deps */
import {Button, Divider, Radio} from "antd"
import cx from "classnames"
import {BaseButton, BaseTextArea, Icon} from "components"
import {FormLabel} from "components/Form"
import {useStudentStaffContactRoles} from "hooks"
import React from "react"
import {useTranslation} from "react-i18next"
import {smsService} from "services"
import {
    DialerRoutingAction,
    NEW_DIALER_ROUTING_ACTION,
    SmsPhoneNumberDialerSettings,
    VoicemailVoice
} from "types/sms-dialer-settings"
import {handleError, toastError} from "helpers"
import {AudioFileSelect} from "./AudioFileSelect"
import {DialerRoutingActionNode} from "./DialerRoutingAction"
import styles from "./PhoneNumberDetails.module.css"

type Props = {
    smsPhoneNumbersId: number
    settings: SmsPhoneNumberDialerSettings
    onChange: React.Dispatch<React.SetStateAction<SmsPhoneNumberDialerSettings>>
    onVoicemailGenerated?: (settings: SmsPhoneNumberDialerSettings) => void
    onVoicemailFileSelected?: (file: File | null) => void
    canEdit: boolean
}

const ALLOWED_AUDIO_EXTENSIONS = ["mp3", "wav"]

const DialerRoutingForm: React.FC<Props> = ({
    smsPhoneNumbersId,
    settings,
    onChange,
    onVoicemailGenerated,
    onVoicemailFileSelected,
    canEdit
}) => {
    const {t} = useTranslation(["settings", "common"])
    const {staffContactRoles: staffRoles} = useStudentStaffContactRoles()
    const [actionIndexBeingEdited, setActionIndexBeingEdited] = React.useState(-1)
    const [isGenerating, setIsGenerating] = React.useState(false)
    const [selectedFileName, setSelectedFileName] = React.useState<string | null>(null)
    const previewObjectUrlRef = React.useRef<string | null>(null)

    const previewSrc = settings.voicemailPlayUrl || settings.voicemailAudioUrl || undefined
    const showAudioPreview = !isGenerating && Boolean(previewSrc)

    const revokePreviewObjectUrl = () => {
        if (previewObjectUrlRef.current) {
            URL.revokeObjectURL(previewObjectUrlRef.current)
            previewObjectUrlRef.current = null
        }
    }

    React.useEffect(() => {
        return () => revokePreviewObjectUrl()
    }, [])

    const handleGenerateVoicemail = async () => {
        const script = settings.voicemailScript?.trim()
        if (!script) {
            toastError(t("smsSettings.dialerSettings.requiredScript"))
            return
        }

        try {
            setIsGenerating(true)
            const result = await smsService.generateVoicemail({
                smsPhoneNumbersId,
                voice: settings.voicemailVoice,
                script
            })
            if (result?.settings) {
                revokePreviewObjectUrl()
                setSelectedFileName(null)
                onVoicemailFileSelected?.(null)
                const nextSettings = {
                    ...settings,
                    voicemailScript: result.settings.voicemailScript,
                    voicemailVoice: result.settings.voicemailVoice,
                    voicemailAudioUrl: result.settings.voicemailAudioUrl,
                    voicemailPlayUrl: result.settings.voicemailPlayUrl
                }
                onChange(nextSettings)
                onVoicemailGenerated?.(nextSettings)
            }
        } catch (error) {
            handleError(error)
            toastError(t("smsSettings.dialerSettings.generateFailure"))
        } finally {
            setIsGenerating(false)
        }
    }

    const handleSelectAudioFile = (file: File) => {
        const fileExt = file.name.split(".").pop()?.toLowerCase()
        if (!fileExt || !ALLOWED_AUDIO_EXTENSIONS.includes(fileExt)) {
            toastError(t("smsSettings.dialerSettings.invalidAudioFormat"))
            return
        }

        revokePreviewObjectUrl()
        const playUrl = URL.createObjectURL(file)
        previewObjectUrlRef.current = playUrl
        setSelectedFileName(file.name)
        onVoicemailFileSelected?.(file)
        onChange({
            ...settings,
            voicemailAudioUrl: null,
            voicemailPlayUrl: playUrl
        })
    }

    const handleAddAction = () => {
        onChange((prev) => ({
            ...prev,
            routingActions: [...prev.routingActions, {...NEW_DIALER_ROUTING_ACTION, order: prev.routingActions.length}]
        }))
        setActionIndexBeingEdited(settings.routingActions.length)
    }

    const handleEditAction = (index: number) => setActionIndexBeingEdited(index)

    const handleCancelAction = () => {
        const current = settings.routingActions[actionIndexBeingEdited]
        if (current && current.id == null) {
            onChange((prev) => ({
                ...prev,
                routingActions: prev.routingActions.filter((_, i) => i !== actionIndexBeingEdited)
            }))
        }
        setActionIndexBeingEdited(-1)
    }

    const handleDoneAction = (action: DialerRoutingAction) => {
        onChange((prev) => ({
            ...prev,
            routingActions: prev.routingActions.map((item, index) =>
                index === actionIndexBeingEdited ? {...action, id: action.id ?? Date.now() + index} : item
            )
        }))
        setActionIndexBeingEdited(-1)
    }

    const handleRemoveAction = (index: number) => {
        onChange((prev) => ({
            ...prev,
            routingActions: prev.routingActions.filter((_, i) => i !== index).map((item, order) => ({...item, order}))
        }))
        setActionIndexBeingEdited(-1)
    }

    const handleVoiceChange = (voice: VoicemailVoice) => {
        onChange((prev) => ({...prev, voicemailVoice: voice}))
    }

    const handleScriptChange = (script: string) => {
        onChange((prev) => ({...prev, voicemailScript: script}))
    }

    return (
        <div className={styles.container}>
            <div className={styles.section}>
                <span className={styles.sectionTitle}>{t("smsSettings.dialerSettings.routingCondition")}</span>
                <div className={styles.sectionContent}>
                    <p className="m-0">{t("smsSettings.dialerSettings.routingConditionDescription")}</p>
                </div>
            </div>

            {settings.routingActions.length > 0 && <div className={cx(styles.sectionDivider, "h-64")} />}

            {settings.routingActions.map((action, index) => (
                <React.Fragment key={action.id ?? `action-${index}`}>
                    <DialerRoutingActionNode
                        beingEdited={actionIndexBeingEdited === index}
                        actionIndex={index}
                        initialAction={action}
                        staffRoles={staffRoles}
                        canEdit={canEdit}
                        onEdit={handleEditAction}
                        onCancel={handleCancelAction}
                        onDone={handleDoneAction}
                        onRemove={handleRemoveAction}
                    />
                    {index < settings.routingActions.length - 1 && (
                        <div className={cx(styles.sectionDivider, "h-64")} />
                    )}
                </React.Fragment>
            ))}

            {actionIndexBeingEdited < 0 && canEdit && (
                <div className="relative">
                    <div className={cx(styles.sectionDivider, "h-96")} />
                    <Button
                        type="default"
                        shape="round"
                        icon={<Icon icon="PLUS" color="#1e90ff" />}
                        onClick={handleAddAction}
                        className={styles.sectionDividerAdd}
                    />
                </div>
            )}

            <Divider className="border-t-2 border-primary my-40" />

            <div className={styles.section}>
                <span className={styles.sectionTitle}>{t("smsSettings.dialerSettings.voicemail")}</span>
                <div className={styles.sectionContent}>
                    <div className={styles.voicemailMethods}>
                        <div className={styles.voicemailMethodColumn}>
                            <span className={styles.voicemailMethodTitle}>
                                {t("smsSettings.dialerSettings.generateAudio")}
                            </span>
                            <div>
                                <FormLabel label={t("smsSettings.dialerSettings.voice")} isRequired />
                                <Radio.Group
                                    className={styles.radioGroup}
                                    value={settings.voicemailVoice}
                                    onChange={(e) => handleVoiceChange(e.target.value)}
                                    disabled={!canEdit}>
                                    <Radio.Button value="male">{t("smsSettings.dialerSettings.male")}</Radio.Button>
                                    <Radio.Button value="female">{t("smsSettings.dialerSettings.female")}</Radio.Button>
                                </Radio.Group>
                            </div>
                            <div>
                                <FormLabel label={t("smsSettings.dialerSettings.script")} isRequired />
                                <BaseTextArea
                                    value={settings.voicemailScript || ""}
                                    onChange={handleScriptChange}
                                    placeholder={t("smsSettings.dialerSettings.scriptPlaceholder")}
                                    disabled={!canEdit}
                                    rows={4}
                                />
                            </div>
                            {canEdit && (
                                <div>
                                    <BaseButton
                                        title={t("smsSettings.dialerSettings.generate")}
                                        onClick={handleGenerateVoicemail}
                                        loading={isGenerating}
                                    />
                                </div>
                            )}
                            {isGenerating && (
                                <div className={styles.notice}>{t("smsSettings.dialerSettings.generatingAudio")}</div>
                            )}
                        </div>

                        <div className={styles.voicemailMethodDivider}>
                            <div className={styles.voicemailMethodDividerLine} />
                            <span>{t("smsSettings.dialerSettings.or")}</span>
                            <div className={styles.voicemailMethodDividerLine} />
                        </div>

                        <div className={styles.voicemailMethodColumn}>
                            <span className={styles.voicemailMethodTitle}>
                                {t("smsSettings.dialerSettings.uploadAudio")}
                            </span>
                            {canEdit && (
                                <AudioFileSelect onSelectFile={handleSelectAudioFile} disabled={isGenerating} />
                            )}
                            {selectedFileName && <div className={styles.uploadedFileName}>{selectedFileName}</div>}
                        </div>
                    </div>

                    {showAudioPreview && (
                        <div>
                            <FormLabel label={t("smsSettings.dialerSettings.audioPreview")} />
                            <audio controls src={previewSrc} className={styles.audioPlayer} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default DialerRoutingForm
